import type { Organization } from '@compass/client/types/organization';
import { ChevronsUpDown } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface OrganizationComboboxProps {
  organizations: Organization[];
  selectedOrganizationId: string | null;
  onOrganizationChange: (organizationId: string) => void;
  disabled?: boolean;
}

/**
 * Searchable organization dropdown for the header
 * Uses Command component for fuzzy search and keyboard navigation
 * Shows search input only when there are 5 or more organizations
 */
export function OrganizationCombobox({
  organizations,
  selectedOrganizationId,
  onOrganizationChange,
  disabled = false,
}: OrganizationComboboxProps) {
  const [open, setOpen] = useState(false);

  const selectedOrganization = useMemo(
    () => organizations.find(o => o.id === selectedOrganizationId),
    [organizations, selectedOrganizationId],
  );

  const showSearch = organizations.length >= 5;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          data-testid="organization-switcher"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-6 px-1 border-none inline-flex items-center gap-1',
            'focus:ring-0 focus:ring-offset-0 focus:outline-hidden',
            'text-sm text-foreground',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          <span>{selectedOrganization?.name || 'Select Organization'}</span>
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0 hover:bg-muted p-0.5 rounded-sm" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[300px]" align="start">
        <Command>
          {showSearch && <CommandInput placeholder="Search organizations..." className="h-9" />}
          <CommandList>
            <CommandEmpty>No organizations found</CommandEmpty>
            <CommandGroup>
              {organizations.map(organization => (
                <CommandItem
                  key={organization.id}
                  value={organization.name}
                  onSelect={() => {
                    if (organization.id) {
                      onOrganizationChange(organization.id);
                      setOpen(false);
                    }
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{organization.name}</span>
                    {organization.id === selectedOrganizationId && (
                      <span className="text-xs text-muted-foreground">✓</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
